import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, runTransaction, writeBatch } from 'firebase/firestore';
import {
  parseCreditCsv, recordToRow, CSV_COLUMNS, balancesFromRecords,
} from '../studentCreditCsv';
import { db, auth } from '../firebase';
import { Award, Settings as SettingsIcon, Plus, Trash2, Edit2, X, Wallet, BarChart3, Upload, Download, FileDown, ArrowUpDown } from 'lucide-react';
import Papa from 'papaparse';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

const generateRowId = () => `r_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

const ITEM_CSV_COLUMNS = ['code', 'nature', 'description', 'balance'];

const emptyRecordForm = () => ({
  classFilter: '',
  sid: '',
  itemCode: '',
  balanceChange: 0,
  date: new Date().toISOString().slice(0, 10),
  remarks: '',
  staffInCharge: '',
});

const StudentCredit = () => {
  const { t } = useT();
  const natureWord = (v) => t(`studentCredit.nature.${String(v || '').toLowerCase()}`, {}, v);
  const { activeSchoolId, isMobile } = useOutletContext();
  const [activeTab, setActiveTab] = useState('record');

  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');
  const [studentList, setStudentList] = useState([]);
  const [staffList, setStaffList] = useState([]);

  // --- Settings: items list + balance mode ---
  const [items, setItems] = useState([]);
  const [balanceMode, setBalanceMode] = useState('yearly'); // 'yearly' | 'lifetime'
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const itemFileInputRef = useRef(null);

  // --- Statistics ---
  const [statsSortOrder, setStatsSortOrder] = useState('desc');

  // --- Records ---
  const [records, setRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [balances, setBalances] = useState({}); // sid -> number, for the currently active mode/year
  const [recordClassFilter, setRecordClassFilter] = useState('');
  const [recordStudentFilter, setRecordStudentFilter] = useState('');
  const [recordNatureFilter, setRecordNatureFilter] = useState('');

  // --- Add/Edit Record modal ---
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [recordForm, setRecordForm] = useState(emptyRecordForm());
  const [savingRecord, setSavingRecord] = useState(false);

  // --- Reference data: academic years, staff, students ---
  useEffect(() => {
    if (!activeSchoolId) return;
    const fetchReference = async () => {
      try {
        const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
        if (yList.length > 0) {
          const current = yList.find(y => y.cur_year) || yList[yList.length - 1];
          setSchoolYear(current.yearId);
        }
      } catch (e) {
        console.error('Error loading academic years', e);
      }

      try {
        const staffSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const sList = [];
        staffSnap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          sList.push({
            initial: data.initial,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial,
            chineseName: data.chineseName || '',
          });
        });
        sList.sort((a, b) => a.name.localeCompare(b.name));
        setStaffList(sList);
      } catch (e) {
        console.error('Error loading staff list', e);
      }

      try {
        const studentSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'));
        const stList = [];
        studentSnap.forEach(d => {
          const data = d.data();
          if (!data.sid) return;
          stList.push({
            sid: data.sid,
            // Needed to stamp the row with who it is about -- a rule cannot resolve a SID to an
            // account, so the addresses have to live on the record itself.
            email: (data.email || d.id || '').toLowerCase(),
            guardianEmails: [data.guardianEmail, data.guardianEmail2]
              .filter(Boolean)
              .map(e => String(e).toLowerCase()),
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || 'Unknown',
            chineseName: data.chineseName || '',
            currentClass: data.currentClass || '',
            classNumber: data.classNumber || '',
          });
        });
        stList.sort((a, b) => (a.currentClass || '').localeCompare(b.currentClass || '', undefined, { numeric: true }) || Number(a.classNumber || 0) - Number(b.classNumber || 0));
        setStudentList(stList);
      } catch (e) {
        console.error('Error loading student list', e);
      }
    };
    fetchReference();
  }, [activeSchoolId]);

  const classOptions = useMemo(() => [...new Set(studentList.map(s => s.currentClass))].filter(Boolean).sort(), [studentList]);
  const studentMap = useMemo(() => Object.fromEntries(studentList.map(s => [s.sid, s])), [studentList]);
  const staffOptions = useMemo(() => staffList.map(s => ({ value: s.initial, label: `${s.name} (${s.initial})` })), [staffList]);
  const itemMap = useMemo(() => Object.fromEntries(items.map(it => [it.code, it])), [items]);

  // --- Settings: fetch/save ---
  const fetchSettings = async () => {
    if (!activeSchoolId) return;
    setSettingsLoaded(false);
    try {
      const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'student_credit_settings'));
      const data = snap.exists() ? snap.data() : {};
      setItems((Array.isArray(data.items) ? data.items : []).map(it => ({ ...it, _rowId: it._rowId || generateRowId() })));
      setBalanceMode(data.balanceMode === 'lifetime' ? 'lifetime' : 'yearly');
    } catch (e) {
      console.error('Error loading student credit settings', e);
    } finally {
      setSettingsLoaded(true);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchoolId]);

  const addItemRow = () => {
    setItems(prev => [...prev, { _rowId: generateRowId(), code: '', nature: 'Award', description: '', balance: 1 }]);
  };
  const updateItemRow = (idx, key, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [key]: val } : it));
  };
  const removeItemRow = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  // Export/import operate on the in-memory items list only -- the user still needs to click "Save Settings"
  // afterwards to persist, same as any other edit made in this table.
  const handleExportItemsCSV = () => {
    const csvData = items.map(it => ({
      code: it.code || '',
      nature: it.nature || '',
      description: it.description || '',
      balance: it.balance ?? 0,
    }));
    if (csvData.length === 0) csvData.push(Object.fromEntries(ITEM_CSV_COLUMNS.map(c => [c, ''])));
    const csv = Papa.unparse(csvData, { columns: ITEM_CSV_COLUMNS });
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student_credit_items.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  // Merges by ID (code): a row whose code matches an existing item updates it in place; a new code is appended.
  const handleImportItemsFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const results = await new Promise((resolve, reject) => {
        Papa.parse(file, { header: true, skipEmptyLines: true, complete: resolve, error: reject });
      });
      const dataRows = results.data;
      if (dataRows.length === 0) { alert(t('studentCredit.noDataRows')); return; }

      let added = 0, updated = 0, skipped = 0;
      setItems(prev => {
        const next = [...prev];
        dataRows.forEach(raw => {
          const rec = Object.fromEntries(Object.entries(raw).map(([k, v]) => [(k || '').trim(), (v ?? '').trim()]));
          const code = (rec.code || '').trim();
          if (!code) { skipped++; return; }
          const nature = rec.nature === 'Punishment' ? 'Punishment' : 'Award';
          const row = { code, nature, description: rec.description || '', balance: Number(rec.balance) || 0 };
          const idx = next.findIndex(it => (it.code || '').toLowerCase() === code.toLowerCase());
          if (idx >= 0) {
            next[idx] = { ...next[idx], ...row };
            updated++;
          } else {
            next.push({ _rowId: generateRowId(), ...row });
            added++;
          }
        });
        return next;
      });
      alert(skipped
        ? t('studentCredit.itemsImportedSkipped', { added, updated, skipped })
        : t('studentCredit.itemsImported', { added, updated }));
    } catch (err) {
      console.error('Error importing items CSV', err);
      alert(t('studentCredit.itemsImportFailed', { message: err.message }));
    }
  };

  const handleSaveSettings = async () => {
    const cleanItems = items.map(it => ({ ...it, code: (it.code || '').trim() })).filter(it => it.code);
    const codesLower = cleanItems.map(it => it.code.toLowerCase());
    const dupes = codesLower.filter((c, i) => codesLower.indexOf(c) !== i);
    if (dupes.length > 0) return alert(t('studentCredit.duplicateIds', { ids: [...new Set(dupes)].join(', ') }));

    setSavingSettings(true);
    try {
      const cleanForSave = cleanItems.map(({ _rowId, ...rest }) => ({
        ...rest,
        balance: Number(rest.balance) || 0,
      }));
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'student_credit_settings'), {
        items: cleanForSave,
        balanceMode,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      }, { merge: true });
      await fetchSettings();
      alert(t('studentCredit.settingsSaved'));
    } catch (e) {
      console.error('Error saving student credit settings', e);
      alert(t('studentCredit.settingsFailed', { message: e.message }));
    }
    setSavingSettings(false);
  };

  // --- Records: fetch (scoped to current school year) ---
  const fetchRecords = async () => {
    if (!activeSchoolId || !schoolYear) { setRecords([]); return; }
    setLoadingRecords(true);
    try {
      const snap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'student_credit_record'), where('schoolYear', '==', schoolYear)));
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.createdAt || '').localeCompare(a.createdAt || ''));
      setRecords(list);
    } catch (e) {
      console.error('Error loading student credit records', e);
    } finally {
      setLoadingRecords(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchoolId, schoolYear]);

  // --- Balances: fetch the map matching the currently active mode (yearly bucket or lifetime) ---
  const fetchBalances = async () => {
    if (!activeSchoolId) return;
    try {
      const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'student_credit_balances'));
      const data = snap.exists() ? snap.data() : {};
      if (balanceMode === 'lifetime') {
        setBalances(data.lifetime || {});
      } else {
        setBalances((data.yearly || {})[schoolYear] || {});
      }
    } catch (e) {
      console.error('Error loading student credit balances', e);
    }
  };

  useEffect(() => {
    if (settingsLoaded) fetchBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSchoolId, schoolYear, balanceMode, settingsLoaded]);

  // Applies delta to a student's balance under BOTH the yearly bucket (for the given schoolYear) and the
  // lifetime bucket, in one transaction, regardless of which mode is currently active -- so switching
  // balanceMode later in Settings never loses history or requires a migration; only which map is
  // displayed changes, since both are always kept up to date.
  const adjustBalance = async (sid, delta, forYear) => {
    if (!delta) return;
    const balancesRef = doc(db, 'schools', activeSchoolId, 'metadata', 'student_credit_balances');
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(balancesRef);
      const data = snap.exists() ? snap.data() : {};
      const yearly = data.yearly || {};
      const yearMap = yearly[forYear] || {};
      const lifetime = data.lifetime || {};
      tx.set(balancesRef, {
        yearly: { ...yearly, [forYear]: { ...yearMap, [sid]: (yearMap[sid] || 0) + delta } },
        lifetime: { ...lifetime, [sid]: (lifetime[sid] || 0) + delta },
      }, { merge: true });
    });
    setBalances(prev => {
      const activeYear = balanceMode === 'lifetime' || forYear === schoolYear;
      if (!activeYear) return prev;
      return { ...prev, [sid]: (prev[sid] || 0) + delta };
    });
  };

  // --- Add/Edit Record modal ---
  const openAddRecordModal = (prefillSid) => {
    setEditingRecordId(null);
    const student = prefillSid ? studentMap[prefillSid] : null;
    setRecordForm({ ...emptyRecordForm(), classFilter: student?.currentClass || '', sid: prefillSid || '' });
    setShowRecordModal(true);
  };

  const openEditRecordModal = (record) => {
    setEditingRecordId(record.id);
    const student = studentMap[record.sid];
    setRecordForm({
      classFilter: student?.currentClass || '',
      sid: record.sid,
      itemCode: record.itemCode || '',
      balanceChange: record.balanceChange ?? 0,
      date: record.date || new Date().toISOString().slice(0, 10),
      remarks: record.remarks || '',
      staffInCharge: record.staffInCharge || '',
    });
    setShowRecordModal(true);
  };

  const recordFormClassStudents = useMemo(() => studentList.filter(s => s.currentClass === recordForm.classFilter), [studentList, recordForm.classFilter]);

  const handleItemChange = (code) => {
    const item = itemMap[code];
    setRecordForm(prev => ({ ...prev, itemCode: code, balanceChange: item ? Number(item.balance) || 0 : prev.balanceChange }));
  };

  const handleSaveRecord = async (e) => {
    e.preventDefault();
    if (!recordForm.sid) return alert(t('studentCredit.needStudent'));
    if (!recordForm.itemCode) return alert(t('studentCredit.needItem'));
    if (!schoolYear) return alert(t('studentCredit.needYear'));
    setSavingRecord(true);
    try {
      const item = itemMap[recordForm.itemCode];
      const newDelta = Number(recordForm.balanceChange) || 0;
      const subject = studentMap[recordForm.sid];
      const payload = {
        sid: recordForm.sid,
        // Always written, even empty: a rule that reads a missing field errors, and an erroring
        // rule denies -- so an omitted guardianEmails would hide the row from everyone but staff.
        studentEmail: subject?.email || '',
        guardianEmails: subject?.guardianEmails || [],
        schoolYear,
        itemCode: recordForm.itemCode,
        nature: item?.nature || '',
        description: item?.description || '',
        balanceChange: newDelta,
        date: recordForm.date,
        remarks: recordForm.remarks.trim(),
        staffInCharge: recordForm.staffInCharge,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.email || 'Unknown',
      };

      if (editingRecordId) {
        const existing = records.find(r => r.id === editingRecordId);
        const oldSid = existing?.sid;
        const oldDelta = existing?.balanceChange || 0;
        const oldYear = existing?.schoolYear || schoolYear;
        await setDoc(doc(db, 'schools', activeSchoolId, 'student_credit_record', editingRecordId), payload, { merge: true });
        if (oldSid && oldSid !== recordForm.sid) {
          // Student changed: fully reverse on the old student, fully apply on the new one.
          await adjustBalance(oldSid, -oldDelta, oldYear);
          await adjustBalance(recordForm.sid, newDelta, schoolYear);
        } else {
          await adjustBalance(recordForm.sid, newDelta - oldDelta, schoolYear);
        }
      } else {
        payload.createdAt = new Date().toISOString();
        payload.createdBy = auth.currentUser?.email || 'Unknown';
        const docRef = doc(collection(db, 'schools', activeSchoolId, 'student_credit_record'));
        await setDoc(docRef, payload);
        await adjustBalance(recordForm.sid, newDelta, schoolYear);
      }

      setShowRecordModal(false);
      await fetchRecords();
    } catch (e) {
      console.error('Error saving student credit record', e);
      alert(t('studentCredit.saveRecordFailed', { message: e.message }));
    }
    setSavingRecord(false);
  };

  const handleDeleteRecord = async (record) => {
    if (!window.confirm(t('studentCredit.deleteRecordConfirm', {
      nature: natureWord(record.nature || ''),
      name: studentMap[record.sid]?.name || record.sid,
    }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'student_credit_record', record.id));
      await adjustBalance(record.sid, -(record.balanceChange || 0), record.schoolYear || schoolYear);
      await fetchRecords();
    } catch (e) {
      console.error('Error deleting student credit record', e);
      alert(t('studentCredit.deleteRecordFailed', { message: e.message }));
    }
  };

  // Rebuilds both the yearly and lifetime balance maps from a full scan of every record ever created
  // (not just the current school year) -- useful after a direct Firestore edit or any suspected drift,
  // matching the Substitution module's "Recalculate Staff Balances" utility.

  // --- CSV in and out -------------------------------------------------------------------------
  //
  // This work arrives in bulk -- a term's lateness, a week's merits -- and typing it one modal at a
  // time is the reason it ends up not being entered at all.
  const fileInputRef = useRef(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleExport = () => {
    const rows = displayRecords.map(r => recordToRow(r, studentMap[r.sid]));
    const csv = Papa.unparse({ fields: CSV_COLUMNS, data: rows.length ? rows : [Object.fromEntries(CSV_COLUMNS.map(c => [c, '']))] });
    // The BOM is what makes Excel open this as UTF-8 rather than mangling every Chinese name.
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // What is on screen, not everything -- the filters are how somebody says which records they
    // wanted, and exporting past them would quietly hand back a different set.
    link.download = `student_credit_${schoolYear || 'all'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleChooseFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so choosing the same file twice still fires
    if (!file) return;
    if (!schoolYear) { alert(t('studentCredit.chooseYearFirst')); return; }
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const { entries, problems } = parseCreditCsv(results.data, {
          students: studentList,
          items,
          schoolYear,
          t,
          today: new Date().toISOString().slice(0, 10),
        });
        setImportPreview({ fileName: file.name, entries, problems });
      },
      error: (err) => {
        console.error('Could not read that file', err);
        alert(t('studentCredit.readFileFailed', { message: err.message }));
      },
    });
  };

  const handleConfirmImport = async () => {
    if (!importPreview || importing) return;
    setImporting(true);
    try {
      const CHUNK = 400; // Firestore caps a batch at 500 writes.
      const stamp = new Date().toISOString();
      const who = auth.currentUser?.email || 'Unknown';
      for (let i = 0; i < importPreview.entries.length; i += CHUNK) {
        const batch = writeBatch(db);
        importPreview.entries.slice(i, i + CHUNK).forEach((en) => {
          // Display-only fields never reach Firestore.
          const { _name, _class, _classNumber, ...body } = en;
          const ref = doc(collection(db, 'schools', activeSchoolId, 'student_credit_record'));
          batch.set(ref, { ...body, createdAt: stamp, createdBy: who, updatedAt: stamp, updatedBy: who });
        });
        await batch.commit();
      }

      // Recomputed, not incremented. The balances are an accumulator that every screen adds to as
      // it writes, and a missed increment in an accumulator is permanent because nothing ever
      // recomputes it unless asked. The substitution module learned this the expensive way: both of
      // its importers wrote records without touching the total, and every school that arranged
      // cover by spreadsheet was carrying a difference nobody could explain.
      const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_credit_record'));
      const all = [];
      snap.forEach(d => all.push(d.data()));
      await setDoc(
        doc(db, 'schools', activeSchoolId, 'metadata', 'student_credit_balances'),
        balancesFromRecords(all),
        { merge: false },
      );

      setImportPreview(null);
      await fetchRecords();
      await fetchBalances();
    } catch (err) {
      console.error('Error importing student credit records', err);
      alert(t('studentCredit.importFailed', { message: err.message }));
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const example = Object.fromEntries(CSV_COLUMNS.map(c => [c, '']));
    const first = studentList[0];
    if (first) { example.SID = first.sid; example.Name = first.name; example.Class = first.currentClass; example.No = first.classNumber; }
    if (items[0]) { example['Item Code'] = items[0].code; example.Item = items[0].description || ''; }
    example.Date = new Date().toISOString().slice(0, 10);
    const csv = Papa.unparse({ fields: CSV_COLUMNS, data: [example] });
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student_credit_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRecalculateBalances = async () => {
    if (!window.confirm(t('studentCredit.recalcConfirm'))) return;
    setRecalculating(true);
    try {
      const snap = await getDocs(collection(db, 'schools', activeSchoolId, 'student_credit_record'));
      const yearly = {};
      const lifetime = {};
      snap.forEach(d => {
        const r = d.data();
        if (!r.sid) return;
        const delta = Number(r.balanceChange) || 0;
        const yr = r.schoolYear || 'unknown';
        yearly[yr] = yearly[yr] || {};
        yearly[yr][r.sid] = (yearly[yr][r.sid] || 0) + delta;
        lifetime[r.sid] = (lifetime[r.sid] || 0) + delta;
      });
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'student_credit_balances'), { yearly, lifetime }, { merge: false });
      await fetchBalances();
      alert(t('studentCredit.recalcDone'));
    } catch (e) {
      console.error('Error recalculating balances', e);
      alert(t('studentCredit.recalcFailed', { message: e.message }));
    }
    setRecalculating(false);
  };

  const displayRecords = useMemo(() => {
    return records.filter(r => {
      if (recordClassFilter && studentMap[r.sid]?.currentClass !== recordClassFilter) return false;
      if (recordStudentFilter && r.sid !== recordStudentFilter) return false;
      if (recordNatureFilter && r.nature !== recordNatureFilter) return false;
      return true;
    });
  }, [records, recordClassFilter, recordStudentFilter, recordNatureFilter, studentMap]);

  const recordFilterClassStudents = useMemo(() => studentList.filter(s => s.currentClass === recordClassFilter), [studentList, recordClassFilter]);

  // --- Statistics: net balance rolled up by class level, by class, and per-student ---
  const statsByLevel = useMemo(() => {
    const map = {};
    studentList.forEach(s => {
      const level = (s.currentClass || '').charAt(0);
      if (!level) return;
      if (!map[level]) map[level] = { key: level, balance: 0, count: 0 };
      map[level].balance += balances[s.sid] || 0;
      map[level].count += 1;
    });
    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
  }, [studentList, balances]);

  const statsByClass = useMemo(() => {
    const map = {};
    studentList.forEach(s => {
      const cls = s.currentClass;
      if (!cls) return;
      if (!map[cls]) map[cls] = { key: cls, balance: 0, count: 0 };
      map[cls].balance += balances[s.sid] || 0;
      map[cls].count += 1;
    });
    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
  }, [studentList, balances]);

  const statsStudentList = useMemo(() => {
    const list = studentList.map(s => ({ ...s, balance: balances[s.sid] || 0 }));
    list.sort((a, b) => statsSortOrder === 'desc' ? b.balance - a.balance : a.balance - b.balance);
    return list;
  }, [studentList, balances, statsSortOrder]);

  const tabs = [
    { id: 'statistics', label: t('studentCredit.tabs.statistics'), icon: BarChart3 },
    { id: 'record', label: t('studentCredit.tabs.record'), icon: Award },
    { id: 'settings', label: t('studentCredit.tabs.settings'), icon: SettingsIcon },
  ];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs Bar */}
      <div className="theme-tabs-bar" style={{ minHeight: '66px' }}>
        <div className="theme-tabs-group">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
                style={{ whiteSpace: 'nowrap' }}
              >
                <div className="theme-tab-icon"><Icon size={18} /></div>
                {tab.label}
              </button>
            );
          })}
        </div>

        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', justifyContent: 'flex-end', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          {activeTab === 'record' && (
            <>
              <input
                ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleChooseFile}
                style={{ display: 'none' }}
              />
              {/* Icon only. These three are occasional, and their labels were what pushed the row
                  onto a second line -- an icon with a tooltip says the same thing in a sixth of the
                  width. Add Record keeps its words: it is the one people came here to press. */}
              <button className="btn-outline btn-icon" onClick={handleDownloadTemplate}
                title={t('studentCredit.templateTitle')} aria-label={t('studentCredit.templateTitle')}>
                <FileDown size={16} />
              </button>
              <button className="btn-outline btn-icon" onClick={handleExport}
                title={t('studentCredit.exportTitle')} aria-label={t('studentCredit.exportTitle')}>
                <Download size={16} />
              </button>
              <button className="btn-outline btn-icon" onClick={() => fileInputRef.current?.click()} disabled={!schoolYear}
                title={t('studentCredit.importTitle')} aria-label={t('studentCredit.importTitle')}>
                <Upload size={16} />
              </button>
            </>
          )}
          {activeTab === 'record' && (
            <button className="btn-primary" onClick={() => openAddRecordModal()} disabled={!schoolYear}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0 0.9rem', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              <Plus size={15} /> Add Record
            </button>
          )}
          {activeTab === 'record' && isMobile && (
            <>
              <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('studentCredit.className')}</label>
                <select className="input-field" value={recordClassFilter} onChange={e => { setRecordClassFilter(e.target.value); setRecordStudentFilter(''); }}>
                  <option value="">{t('common.allClasses')}</option>
                  {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('studentCredit.student')}</label>
                <select className="input-field" value={recordStudentFilter} onChange={e => setRecordStudentFilter(e.target.value)} disabled={!recordClassFilter}>
                  <option value="">{t('studentCredit.allStudents')}</option>
                  {recordFilterClassStudents.map(s => <option key={s.sid} value={s.sid}>{s.classNumber} - {s.name} {s.chineseName}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('studentCredit.natureLabel')}</label>
                <select className="input-field" value={recordNatureFilter} onChange={e => setRecordNatureFilter(e.target.value)}>
                  <option value="">{t('studentCredit.all')}</option>
                  <option value="Award">{t('studentCredit.nature.award')}</option>
                  <option value="Punishment">{t('studentCredit.nature.punishment')}</option>
                </select>
              </div>
            </>
          )}
          {(activeTab === 'record' || activeTab === 'statistics') && (
            <select className="input-field" value={schoolYear} onChange={e => setSchoolYear(e.target.value)}
              style={{ width: '140px', padding: '0 0.6rem', flexShrink: 0 }}>
              {academicYears.length === 0 ? (
                <option value="">{t('studentCredit.noYears')}</option>
              ) : academicYears.map(y => <option key={y.uid} value={y.yearId}>{y.yearId}</option>)}
            </select>
          )}
        </div>
        </HeaderActionsPortal>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>
        {activeTab === 'statistics' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>
              {t('studentCredit.netBalanceStats')} {balanceMode === 'lifetime'
                ? t('studentCredit.lifetimeSuffix')
                : schoolYear && t('studentCredit.yearSuffix', { year: schoolYear })}
            </h2>

            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 260px', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', margin: 0, padding: '1rem 1.25rem 0.75rem' }}>{t('studentCredit.byClassLevel')}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.level')}</th>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.studentsCol')}</th>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.netBalance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsByLevel.length === 0 ? (
                      <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.noData')}</td></tr>
                    ) : statsByLevel.map(row => (
                      <tr key={row.key} style={{ borderTop: '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{row.key}</td>
                        <td style={{ padding: '0.6rem 1.25rem' }}>{row.count}</td>
                        <td style={{ padding: '0.6rem 1.25rem', fontWeight: '600', color: row.balance > 0 ? 'var(--color-success)' : row.balance < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                          {row.balance > 0 ? '+' : ''}{row.balance}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ flex: '1 1 260px', backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', margin: 0, padding: '1rem 1.25rem 0.75rem' }}>{t('studentCredit.byClass')}</h3>
                <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                        <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.className')}</th>
                        <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.studentsCol')}</th>
                        <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.netBalance')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsByClass.length === 0 ? (
                        <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.noData')}</td></tr>
                      ) : statsByClass.map(row => (
                        <tr key={row.key} style={{ borderTop: '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                          <td style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{row.key}</td>
                          <td style={{ padding: '0.6rem 1.25rem' }}>{row.count}</td>
                          <td style={{ padding: '0.6rem 1.25rem', fontWeight: '600', color: row.balance > 0 ? 'var(--color-success)' : row.balance < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                            {row.balance > 0 ? '+' : ''}{row.balance}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem 0.75rem' }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '700', margin: 0 }}>{t('studentCredit.students', { count: statsStudentList.length })}</h3>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setStatsSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}
                >
                  <ArrowUpDown size={14} /> {statsSortOrder === 'desc' ? t('studentCredit.highestFirst') : t('studentCredit.lowestFirst')}
                </button>
              </div>
              <div className="table-container" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.className')}</th>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.no')}</th>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.engName')}</th>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.cName')}</th>
                      <th style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{t('studentCredit.netBalance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statsStudentList.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.noStudentsFound')}</td></tr>
                    ) : statsStudentList.map((s, i) => (
                      <tr key={s.sid} style={{ borderTop: i === 0 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.6rem 1.25rem' }}>{s.currentClass}</td>
                        <td style={{ padding: '0.6rem 1.25rem' }}>{s.classNumber}</td>
                        <td style={{ padding: '0.6rem 1.25rem', fontWeight: '600' }}>{s.name}</td>
                        <td style={{ padding: '0.6rem 1.25rem' }}>{s.chineseName || '-'}</td>
                        <td style={{ padding: '0.6rem 1.25rem', fontWeight: '600', color: s.balance > 0 ? 'var(--color-success)' : s.balance < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                          {s.balance > 0 ? '+' : ''}{s.balance}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : activeTab === 'record' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>Student Credit Records {schoolYear && `(${schoolYear})`}</h2>

            {!isMobile && (
              <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '1rem 1.5rem', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('studentCredit.className')}</label>
                <select className="input-field" value={recordClassFilter} onChange={e => { setRecordClassFilter(e.target.value); setRecordStudentFilter(''); }}>
                  <option value="">{t('common.allClasses')}</option>
                  {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('studentCredit.student')}</label>
                <select className="input-field" value={recordStudentFilter} onChange={e => setRecordStudentFilter(e.target.value)} disabled={!recordClassFilter}>
                  <option value="">{t('studentCredit.allStudents')}</option>
                  {recordFilterClassStudents.map(s => <option key={s.sid} value={s.sid}>{s.classNumber} - {s.name} {s.chineseName}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.4rem' }}>{t('studentCredit.natureLabel')}</label>
                <select className="input-field" value={recordNatureFilter} onChange={e => setRecordNatureFilter(e.target.value)}>
                  <option value="">{t('studentCredit.all')}</option>
                  <option value="Award">{t('studentCredit.nature.award')}</option>
                  <option value="Punishment">{t('studentCredit.nature.punishment')}</option>
                </select>
              </div>
              {recordStudentFilter && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--color-bg-app)', padding: '0.6rem 1rem', borderRadius: 'var(--radius-md)' }}>
                  <Wallet size={18} color="var(--color-primary)" />
                  <div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.currentBalance', { scope: balanceMode === 'lifetime' ? t('studentCredit.lifetime') : schoolYear })}</div>
                    <div style={{ fontWeight: '700', fontSize: '1.1rem', color: (balances[recordStudentFilter] || 0) > 0 ? 'var(--color-success)' : (balances[recordStudentFilter] || 0) < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                      {(balances[recordStudentFilter] || 0) > 0 ? '+' : ''}{balances[recordStudentFilter] || 0}
                    </div>
                  </div>
                </div>
              )}
              </div>
            )}

            {isMobile && recordStudentFilter && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--color-bg-card)', padding: '0.6rem 0.75rem' }}>
                <Wallet size={18} color="var(--color-primary)" />
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.currentBalance', { scope: balanceMode === 'lifetime' ? t('studentCredit.lifetime') : schoolYear })}</div>
                  <div style={{ fontWeight: '700', fontSize: '1.1rem', color: (balances[recordStudentFilter] || 0) > 0 ? 'var(--color-success)' : (balances[recordStudentFilter] || 0) < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                    {(balances[recordStudentFilter] || 0) > 0 ? '+' : ''}{balances[recordStudentFilter] || 0}
                  </div>
                </div>
              </div>
            )}

            <div className="table-container" style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--color-bg-app)', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.date')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.className')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.no')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.engName')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.cName')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.item')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.natureLabel')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.balance')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.remarks')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{t('studentCredit.staff')}</th>
                    <th style={{ padding: '0.75rem 1rem', fontWeight: '600', width: '90px' }}>{t('studentCredit.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRecords ? (
                    <tr><td colSpan="11" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{t('common.loading')}</td></tr>
                  ) : displayRecords.length === 0 ? (
                    <tr><td colSpan="11" style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>{records.length === 0 ? t('studentCredit.noRecordsThisYear') : t('studentCredit.noRecordsMatch')}</td></tr>
                  ) : displayRecords.map((r, i) => {
                    const st = studentMap[r.sid];
                    return (
                      <tr key={r.id} style={{ borderBottom: i === displayRecords.length - 1 ? 'none' : '1px solid var(--color-border)', fontSize: '0.875rem' }}>
                        <td style={{ padding: '0.75rem 1rem' }}>{r.date || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{st?.currentClass || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{st?.classNumber || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '600' }}>{st?.name || r.sid}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{st?.chineseName || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{r.description || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <span style={{ padding: '0.15rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: r.nature === 'Award' ? 'var(--color-success-light)' : 'var(--color-danger-light)', color: r.nature === 'Award' ? 'var(--color-success)' : 'var(--color-danger)' }}>
                            {r.nature || '-'}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: (r.balanceChange || 0) > 0 ? 'var(--color-success)' : (r.balanceChange || 0) < 0 ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                          {(r.balanceChange || 0) > 0 ? '+' : ''}{r.balanceChange || 0}
                        </td>
                        <td style={{ padding: '0.75rem 1rem', color: 'var(--color-text-muted)' }}>{r.remarks || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>{r.staffInCharge || '-'}</td>
                        <td style={{ padding: '0.75rem 1rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button onClick={() => openEditRecordModal(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }} title={t('common.edit')}>
                              <Edit2 size={16} />
                            </button>
                            <button onClick={() => handleDeleteRecord(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }} title={t('common.delete')}>
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-primary" onClick={handleSaveSettings} disabled={savingSettings || !settingsLoaded} style={{ padding: '0.5rem 1.25rem' }}>
                {savingSettings ? t('common.saving') : t('studentCredit.saveSettings')}
              </button>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: '0 0 1rem' }}>{t('studentCredit.balanceMode')}</h3>
              <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="radio" name="balanceMode" checked={balanceMode === 'yearly'} onChange={() => setBalanceMode('yearly')} />
                  {t('studentCredit.resetEachYear')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="radio" name="balanceMode" checked={balanceMode === 'lifetime'} onChange={() => setBalanceMode('lifetime')} />
                  {t('studentCredit.lifetimeRunningTotal')}
                </label>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0 }}>
                Both totals are always tracked in the background regardless of this setting — switching modes here just changes which one is shown as "Current Balance", with nothing lost either way.
              </p>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.75rem' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0, whiteSpace: 'nowrap' }}>{t('studentCredit.itemsTitle', { count: items.length })}</h3>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <input type="file" ref={itemFileInputRef} accept=".csv" style={{ display: 'none' }} onChange={handleImportItemsFile} />
                  <button type="button" className="btn-outline" onClick={() => itemFileInputRef.current?.click()} title={t('studentCredit.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', width: 'auto', whiteSpace: 'nowrap' }}>
                    <Upload size={16} />
                  </button>
                  <button type="button" className="btn-outline" onClick={handleExportItemsCSV} disabled={items.length === 0} title={t('studentCredit.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', width: 'auto', whiteSpace: 'nowrap' }}>
                    <Download size={16} />
                  </button>
                  <button type="button" className="btn-outline" onClick={addItemRow} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.75rem', width: 'auto', whiteSpace: 'nowrap' }}>
                    <Plus size={16} /> Add
                  </button>
                </div>
              </div>
              <div className="table-container" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead style={{ backgroundColor: 'var(--color-bg-app)' }}>
                    <tr>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('studentCredit.itemCode')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('studentCredit.natureLabel')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('studentCredit.description')}</th>
                      <th style={{ padding: '0.6rem 0.75rem', fontSize: '0.8rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('studentCredit.balance')}</th>
                      <th style={{ width: '48px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.25rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.noItems')}</td></tr>
                    ) : items.map((it, idx) => (
                      <tr key={it._rowId} style={{ borderTop: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <input className="input-field" value={it.code || ''} placeholder={t('studentCredit.codePlaceholder')} onChange={e => updateItemRow(idx, 'code', e.target.value)} style={{ minWidth: '120px' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <select className="input-field" value={it.nature || 'Award'} onChange={e => updateItemRow(idx, 'nature', e.target.value)} style={{ minWidth: '130px' }}>
                            <option value="Award">{t('studentCredit.nature.award')}</option>
                            <option value="Punishment">{t('studentCredit.nature.punishment')}</option>
                          </select>
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <input className="input-field" value={it.description || ''} placeholder={t('studentCredit.descriptionPlaceholder')} onChange={e => updateItemRow(idx, 'description', e.target.value)} style={{ minWidth: '200px' }} />
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem' }}>
                          <input type="number" className="input-field" value={it.balance ?? 0} onChange={e => updateItemRow(idx, 'balance', e.target.value)} style={{ width: '90px' }} />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button type="button" onClick={() => removeItemRow(idx)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex' }}>
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ backgroundColor: 'var(--color-bg-card)', padding: isMobile ? '0.75rem' : '1.5rem', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: '0 0 0.5rem 0' }}>{t('studentCredit.recalcTitle')}</h3>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.recalcIntro')}</p>
              </div>
              <button type="button" className="btn-outline" onClick={handleRecalculateBalances} disabled={recalculating}>
                {recalculating ? t('studentCredit.recalculating') : t('studentCredit.recalcNow')}
              </button>
            </div>
          </div>
        )}
      </div>


      {/* --- Import preview: read before anything is written --- */}
      {importPreview && (
        <div onClick={() => { if (!importing) setImportPreview(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 130, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '740px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('studentCredit.beforeImporting')}</h3>
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {importPreview.fileName} &middot; filed under {schoolYear}
                </span>
              </div>
              <button type="button" onClick={() => setImportPreview(null)} aria-label={t('common.close')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <b style={{ fontSize: '1.5rem', lineHeight: 1, color: 'var(--color-primary)' }}>{importPreview.entries.length}</b>
                  <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.recordsToAdd')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <b style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                    {importPreview.entries.reduce((n, e) => n + (Number(e.balanceChange) || 0), 0)}
                  </b>
                  <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.netPoints')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <b style={{ fontSize: '1.5rem', lineHeight: 1, color: importPreview.problems.some(p => !p.warning) ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                    {importPreview.problems.filter(p => !p.warning).length}
                  </b>
                  <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t('studentCredit.rowsSkipped')}</span>
                </div>
              </div>

              {/* Every import is an addition. Said plainly, because a spreadsheet imported twice is
                  the easiest mistake to make and there is nothing on the record to notice it. */}
              <div style={{ padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-info-bg)', color: 'var(--color-info-text)', fontSize: '0.82rem', lineHeight: 1.6 }}>
                {t('studentCredit.importAdds')}
              </div>

              {importPreview.entries.length === 0 && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger-text)', fontSize: '0.82rem' }}>
                  {t('studentCredit.nothingUsable')}
                </div>
              )}

              {importPreview.problems.length > 0 && (
                <div>
                  <b style={{ fontSize: '0.85rem' }}>{t('studentCredit.problems', { count: importPreview.problems.length })}</b>
                  <div style={{ marginTop: '0.4rem', maxHeight: '13rem', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    {importPreview.problems.map((pr, i) => (
                      <div key={`${pr.line}-${i}`}
                        style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
                        <span style={{ display: 'inline-flex', padding: '0.05rem 0.4rem', borderRadius: '999px', fontSize: '0.68rem', fontWeight: '700', backgroundColor: pr.warning ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)', color: pr.warning ? 'var(--color-warning-text)' : 'var(--color-danger-text)' }}>
                          {pr.warning ? t('studentCredit.kept') : t('studentCredit.skipped')}
                        </span>
                        <span style={{ color: 'var(--color-text-muted)', minWidth: '3.5rem' }}>{t('studentCredit.row', { line: pr.line })}</span>
                        <span>{pr.who ? `${pr.who}: ` : ''}{pr.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importPreview.entries.length > 0 && (
                <div>
                  <b style={{ fontSize: '0.85rem' }}>{t('studentCredit.whatWillBeAdded')}</b>
                  <div style={{ marginTop: '0.4rem', maxHeight: '16rem', overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                          <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: '600' }}>{t('studentCredit.date')}</th>
                          <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: '600' }}>{t('studentCredit.student')}</th>
                          <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: '600' }}>{t('studentCredit.item')}</th>
                          <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontWeight: '600' }}>{t('studentCredit.points')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.entries.map((en, i) => (
                          <tr key={`${en.sid}-${en.date}-${i}`}>
                            <td style={{ padding: '0.35rem 0.6rem', borderTop: '1px solid var(--color-border)' }}>{en.date}</td>
                            <td style={{ padding: '0.35rem 0.6rem', borderTop: '1px solid var(--color-border)' }}>
                              {[en._class, en._classNumber].filter(Boolean).join('-')} {en._name}
                            </td>
                            <td style={{ padding: '0.35rem 0.6rem', borderTop: '1px solid var(--color-border)' }}>{en.description || en.itemCode}</td>
                            <td style={{ padding: '0.35rem 0.6rem', borderTop: '1px solid var(--color-border)', textAlign: 'right', fontWeight: '600', color: en.balanceChange < 0 ? 'var(--color-danger)' : 'var(--color-success-text)' }}>
                              {en.balanceChange > 0 ? `+${en.balanceChange}` : en.balanceChange}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setImportPreview(null)} disabled={importing}
                  style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>{t('common.cancel')}</button>
                <button type="button" className="btn-primary" onClick={handleConfirmImport}
                  disabled={importing || importPreview.entries.length === 0}
                  style={{ width: 'auto', padding: '0.55rem 1.1rem' }}>
                  {importing ? t('studentCredit.importing') : t('studentCredit.addRecords', { count: importPreview.entries.length })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit Record Modal */}
      {showRecordModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '520px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>{editingRecordId ? t('studentCredit.editRecord') : t('studentCredit.addRecord')}</h3>
              <button type="button" onClick={() => setShowRecordModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveRecord} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentCredit.className')}</label>
                  <select className="input-field" value={recordForm.classFilter} onChange={e => setRecordForm(prev => ({ ...prev, classFilter: e.target.value, sid: '' }))}>
                    <option value="">{t('studentCredit.selectClass')}</option>
                    {classOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: 2 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentCredit.student')}</label>
                  <select className="input-field" value={recordForm.sid} onChange={e => setRecordForm(prev => ({ ...prev, sid: e.target.value }))} disabled={!recordForm.classFilter}>
                    <option value="">{t('common.selectStudent')}</option>
                    {recordFormClassStudents.map(s => <option key={s.sid} value={s.sid}>{s.classNumber} - {s.name} {s.chineseName}</option>)}
                  </select>
                </div>
              </div>

              {recordForm.sid && (
                <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {t('studentCredit.currentBalanceLine', { scope: balanceMode === 'lifetime' ? t('studentCredit.lifetime') : schoolYear })} <strong>{(balances[recordForm.sid] || 0) > 0 ? '+' : ''}{balances[recordForm.sid] || 0}</strong>
                </p>
              )}

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentCredit.item')}</label>
                <select className="input-field" value={recordForm.itemCode} onChange={e => handleItemChange(e.target.value)}>
                  <option value="">-- Select Item --</option>
                  <optgroup label="Award">
                    {items.filter(it => it.nature === 'Award').map(it => <option key={it.code} value={it.code}>{it.code} - {it.description} ({it.balance > 0 ? '+' : ''}{it.balance})</option>)}
                  </optgroup>
                  <optgroup label="Punishment">
                    {items.filter(it => it.nature === 'Punishment').map(it => <option key={it.code} value={it.code}>{it.code} - {it.description} ({it.balance > 0 ? '+' : ''}{it.balance})</option>)}
                  </optgroup>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentCredit.balance')}</label>
                  <input type="number" className="input-field" value={recordForm.balanceChange} onChange={e => setRecordForm(prev => ({ ...prev, balanceChange: e.target.value }))} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentCredit.date')}</label>
                  <input type="date" className="input-field" value={recordForm.date} onChange={e => setRecordForm(prev => ({ ...prev, date: e.target.value }))} required />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentCredit.staffInCharge')}</label>
                <select className="input-field" value={recordForm.staffInCharge} onChange={e => setRecordForm(prev => ({ ...prev, staffInCharge: e.target.value }))}>
                  <option value="">-- Select --</option>
                  {staffOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('studentCredit.remarksOptional')}</label>
                <textarea className="input-field" rows={2} value={recordForm.remarks} onChange={e => setRecordForm(prev => ({ ...prev, remarks: e.target.value }))} style={{ resize: 'vertical', height: 'auto', minHeight: '60px' }} />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowRecordModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={savingRecord}>{savingRecord ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentCredit;
